/**
 * Frontend Money-Display-Layer (ADR-0007 FE-Mirror).
 *
 * String/BigInt-Cent-Math als Spiegel zur Backend-`src/core/money.ts`-Facade.
 * Adressiert F-505, F-801, F-802 — eradiziert die JS-Float-Pfade in FE-Routes
 * (computeSummen, BV-Kalkulator, ModulDetailCard, Entwurf-Summen).
 *
 * Konvention identisch zum Backend:
 *   - Money: Decimal-String mit Skala 2 ("123.45")
 *   - Quantity: Decimal-String mit Skala 3 ("1.500")
 *   - Percent: Decimal-String mit Skala 2 ("19.00")
 *   - Rundung: halfEven (Banker's Rounding) — exakt wie BE multiplyByQuantity.
 *
 * Kein dinero-Import — BigInt reicht und hält das Bundle <2 KB.
 */

const DECIMAL_RE_2 = /^-?\d+(\.\d{1,2})?$/;
const DECIMAL_RE_3 = /^-?\d+(\.\d{1,3})?$/;

function decimalToBigInt(s: string, targetScale: number): bigint {
  const negative = s.startsWith('-');
  const abs = negative ? s.slice(1) : s;
  const [whole, frac = ''] = abs.split('.');
  const fracPadded = (frac + '0'.repeat(targetScale)).slice(0, targetScale);
  const combined = (whole ?? '0') + fracPadded;
  const value = BigInt(combined);
  return negative ? -value : value;
}

function bigIntToDecimal(amount: bigint, scale: number): string {
  if (scale === 0) return amount.toString();
  const negative = amount < 0n;
  const abs = negative ? -amount : amount;
  const s = abs.toString().padStart(scale + 1, '0');
  const cut = s.length - scale;
  return `${negative ? '-' : ''}${s.slice(0, cut)}.${s.slice(cut)}`;
}

/**
 * BigInt-Division mit halfEven-Rundung (Banker's). Gibt floor(a/b) plus
 * Rundungs-Korrektur zurück. Identisch zum Backend `transformScale(_, _, halfEven)`.
 */
function divHalfEven(a: bigint, b: bigint): bigint {
  if (b <= 0n) throw new Error('divHalfEven: divisor muss > 0 sein');
  const negative = a < 0n;
  const aAbs = negative ? -a : a;
  const q = aAbs / b;
  const r = aAbs % b;
  const doubleR = r * 2n;
  let rounded: bigint;
  if (doubleR < b) rounded = q;
  else if (doubleR > b) rounded = q + 1n;
  else rounded = q % 2n === 0n ? q : q + 1n;
  return negative ? -rounded : rounded;
}

function isDecimalString2(s: string): boolean {
  return DECIMAL_RE_2.test(s);
}

function isDecimalString3(s: string): boolean {
  return DECIMAL_RE_3.test(s);
}

export function isMoneyString(s: string): boolean {
  return isDecimalString2(s);
}

export function normalizeMoney(s: string): string {
  if (!isDecimalString2(s)) throw new Error(`Ungültiger Geldbetrag: ${s}`);
  return bigIntToDecimal(decimalToBigInt(s, 2), 2);
}

export const ZERO_MONEY = '0.00';

/**
 * Summiert zwei Money-Strings (Skala 2) ohne Float-Roundtrip.
 */
export function addDecimals(a: string, b: string): string {
  const aBi = decimalToBigInt(a, 2);
  const bBi = decimalToBigInt(b, 2);
  return bigIntToDecimal(aBi + bBi, 2);
}

/**
 * `m * n` mit n als nicht-negativem Integer (kein Skalen-Wechsel, exakt).
 * Beispiel: multiplyByInteger('33.33', 12) === '399.96'.
 */
export function multiplyByInteger(m: string, n: number): string {
  if (!Number.isInteger(n) || n < 0) {
    throw new Error(`multiplyByInteger: n muss nicht-negativer Integer sein, got ${n}`);
  }
  const bi = decimalToBigInt(m, 2);
  return bigIntToDecimal(bi * BigInt(n), 2);
}

/**
 * `m / n` mit n positivem Integer, halfEven-gerundet auf Skala 2.
 * Beispiel: divideByInteger('100.00', 12) === '8.33' (kein 8.33333…-Drift).
 */
export function divideByInteger(m: string, n: number): string {
  if (!Number.isInteger(n) || n <= 0) {
    throw new Error(`divideByInteger: n muss positiver Integer sein, got ${n}`);
  }
  const bi = decimalToBigInt(m, 2);
  return bigIntToDecimal(divHalfEven(bi, BigInt(n)), 2);
}

/**
 * `price * quantity`, gerundet halfEven auf Money-Skala 2.
 * `quantity` ist Decimal-String mit max. 3 Nachkommastellen ('1.500').
 * Beispiel: multiplyByQuantity('12.34', '1.500') === '18.51'.
 */
export function multiplyByQuantity(price: string, qty: string): string {
  if (!isDecimalString3(qty)) throw new Error(`Ungültige Menge: ${qty}`);
  const priceBi = decimalToBigInt(price, 2); // scale=2
  const qtyBi = decimalToBigInt(qty, 3); // scale=3
  // Produkt hat scale=5; auf scale=2 reduzieren = /10^3 mit halfEven.
  const product = priceBi * qtyBi;
  if (product < 0n) {
    return `-${bigIntToDecimal(divHalfEven(-product, 1000n), 2)}`;
  }
  return bigIntToDecimal(divHalfEven(product, 1000n), 2);
}

/**
 * `base * percent / 100`, halfEven auf Money-Skala 2.
 * Beispiel: applyPercent('100.00', '19.00') === '19.00'.
 */
export function applyPercent(base: string, pct: string): string {
  if (!isDecimalString2(pct)) throw new Error(`Ungültiger Prozentwert: ${pct}`);
  const baseBi = decimalToBigInt(base, 2); // scale=2
  const pctBi = decimalToBigInt(pct, 2); // scale=2
  // base (scale=2) * pct (scale=2) als Faktor /100 = scale=4 → auf scale=2 (/10^2 halfEven)
  const product = baseBi * pctBi;
  if (product < 0n) {
    return `-${bigIntToDecimal(divHalfEven(-product, 10000n), 2)}`;
  }
  return bigIntToDecimal(divHalfEven(product, 10000n), 2);
}

/**
 * `base * (1 - percent/100)`, halfEven auf Money-Skala 2.
 * Identisch zur Rabatt-Logik in computeSummen.
 */
export function applyDiscount(base: string, pct: string): string {
  const abzug = applyPercent(base, pct);
  const baseBi = decimalToBigInt(base, 2);
  const abzugBi = decimalToBigInt(abzug, 2);
  return bigIntToDecimal(baseBi - abzugBi, 2);
}

/**
 * Wirft, wenn Input keine Money-Form `YYYY.CC` mit max. 2 Nachkommastellen
 * hat. Wir parsen NICHT durch JS-`Number` — Float-Drift soll explizit
 * sichtbar werden.
 */
export function parseMoneyOr(s: string | undefined | null, fallback: string): string {
  const v = (s ?? '').trim();
  if (v === '') return fallback;
  if (!isDecimalString2(v)) return fallback;
  return normalizeMoney(v);
}

export function parsePercentOr(s: string | undefined | null, fallback: string): string {
  return parseMoneyOr(s, fallback);
}

export function parseQuantityOr(s: string | undefined | null, fallback: string): string {
  const v = (s ?? '').trim();
  if (v === '') return fallback;
  if (!isDecimalString3(v)) return fallback;
  return bigIntToDecimal(decimalToBigInt(v, 3), 3);
}

/**
 * Eingabe für Rechnungs-/Angebots-Form-Positionen. Strings wie eingetippt
 * (User-Input). Leere Strings werden als 0/0/19/0 behandelt — analog zum
 * Backend, das `optional()` mit Defaults behandelt.
 */
export interface PositionForSummen {
  menge: string;
  einzelpreisNetto: string;
  ustSatz: string;
  rabattProzent?: string;
}

/**
 * Cent-genaue Brutto-/Netto-/USt-Aggregation pro Position. Ersetzt das
 * Float-`computeSummen` aus rechnungen.neu.tsx + angebote.neu.tsx.
 *
 * Pro Position gilt:
 *   nettoVorRabatt = einzelpreisNetto * menge   (halfEven scale=2)
 *   netto          = nettoVorRabatt * (1 - rabatt/100)
 *   ust            = netto * ustSatz / 100
 * Summen werden cent-genau aufaddiert (kein Float-Akkumulator).
 */
export function computeSummen(positionen: ReadonlyArray<PositionForSummen>): {
  netto: string;
  ust: string;
  brutto: string;
} {
  let netto = ZERO_MONEY;
  let ust = ZERO_MONEY;
  for (const p of positionen) {
    const menge = parseQuantityOr(p.menge, '0.000');
    const einzel = parseMoneyOr(p.einzelpreisNetto, ZERO_MONEY);
    const rabatt = parsePercentOr(p.rabattProzent ?? '', ZERO_MONEY);
    const ustSatz = parsePercentOr(p.ustSatz, ZERO_MONEY);
    const posVorRabatt = multiplyByQuantity(einzel, menge);
    const posNetto = applyDiscount(posVorRabatt, rabatt);
    const posUst = applyPercent(posNetto, ustSatz);
    netto = addDecimals(netto, posNetto);
    ust = addDecimals(ust, posUst);
  }
  return { netto, ust, brutto: addDecimals(netto, ust) };
}

/**
 * Formatiert einen Money-String als deutsche EUR-Anzeige
 * ("1.234,56" — Tausender-Punkt, Komma als Dezimaltrenner).
 * Ersetzt `Number.toLocaleString('de-DE', …)` in den Routes.
 */
export function formatEuroDe(s: string): string {
  if (!isDecimalString2(s)) return s;
  const negative = s.startsWith('-');
  const abs = negative ? s.slice(1) : s;
  const [whole = '0', frac = '00'] = abs.split('.');
  const fracPadded = (frac + '00').slice(0, 2);
  const wholeWithSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negative ? '-' : ''}${wholeWithSep},${fracPadded}`;
}
